// Tiny theme toggle. Persists choice in localStorage. Without explicit
// choice the CSS @media (prefers-color-scheme) handles defaults so first
// visit honors OS preference. CC-style: no fanfare, just a small
// sun/moon glyph.

import React, { useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

function readTheme(): Theme {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('datahub-theme') : null;
  return (stored === 'light' || stored === 'dark' || stored === 'system') ? stored : 'system';
}

function applyTheme(t: Theme): void {
  const root = document.documentElement;
  if (t === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', t);
}

export default function ThemeToggle(): React.ReactElement {
  const [theme, setTheme] = useState<Theme>(readTheme);

  useEffect(() => {
    applyTheme(theme);
    if (typeof localStorage !== 'undefined') localStorage.setItem('datahub-theme', theme);
  }, [theme]);

  // Apply once on mount BEFORE first paint (avoids FOUC).
  useEffect(() => {
    applyTheme(readTheme());
  }, []);

  function next(): Theme {
    return theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
  }
  const label = theme === 'light' ? 'light' : theme === 'dark' ? 'dark' : 'system';
  const title = `Theme: ${theme}. Click to cycle (light → dark → system).`;

  return (
    <button
      onClick={() => setTheme(next())}
      title={title}
      style={{
        background: 'transparent', border: '1px solid var(--rail-line)',
        borderRadius: 4, padding: '4px 10px', fontSize: 11,
        fontFamily: 'var(--mono)', color: 'var(--rail-ink-2)',
        cursor: 'pointer',
      }}
    >{label}</button>
  );
}
